import { z } from "zod";
import { cleanCaption } from "./captions.js";
import { IMAGE_PEOPLE_OPTIONS } from "./imagePeople.js";
import { CREATIVE_STRATEGIES } from "./artDirection.js";
// =============================================================================
// Request schemas. Every API route validates through one of these (spec §11),
// so an unvalidated body can never reach a service.
// =============================================================================
export const registerSchema = z.object({
    name: z.string().min(2).max(120),
    email: z.string().email().max(200),
    password: z.string().min(8).max(200),
    /** First workspace is created during sign-up; defaults to the user's name. */
    workspaceName: z.string().min(2).max(120).optional(),
    country: z.string().length(2).optional(),
    timezone: z.string().max(80).optional(),
});
export const loginSchema = z.object({
    email: z.string().email().max(200),
    password: z.string().min(1).max(200),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(10),
});
export const verifyEmailSchema = z.object({
    token: z.string().min(16).max(200),
});
export const forgotPasswordSchema = z.object({
    email: z.string().email().max(200),
});
/**
 * The same minimum as registration, deliberately.
 *
 * A reset form that accepts a weaker password than sign-up is a way around
 * the sign-up rule, and it is the form people reach for when they are in a
 * hurry and have just been locked out.
 */
export const resetPasswordSchema = z.object({
    token: z.string().min(16).max(200),
    password: z.string().min(8).max(200),
});
/**
 * Deleting your own account.
 *
 * The password is the whole payload. No "type DELETE to confirm" box: that
 * tests reading comprehension, while a password tests that the person at the
 * keyboard is the account holder — which is what actually matters when the
 * action destroys live page credentials.
 *
 * No minimum length here, unlike the reset schema. This is checked against an
 * existing hash, and an account whose password predates a rule change must
 * still be deletable by its owner.
 */
export const deleteAccountSchema = z.object({
    password: z.string().min(1).max(200),
});
export const createWorkspaceSchema = z.object({
    name: z.string().min(2).max(120),
    country: z.string().length(2).optional(),
    timezone: z.string().max(80).optional(),
});
export const updateWorkspaceSchema = z.object({
    name: z.string().min(2).max(120).optional(),
    country: z.string().length(2).optional(),
    timezone: z.string().max(80).optional(),
});
/**
 * How far AI may go with this brand's images.
 *
 * Not a cosmetic preference. An image produced by the image model carries
 * IPTC `DigitalSourceType` metadata and an invisible SynthID watermark, and
 * Meta reads the metadata to attach its "AI info" label to the post. A brand
 * that does not want that label has to publish images that are not AI images.
 *
 * Captions are unaffected either way — platforms label images, not text — so
 * `off` still gets AI-written posts, just with the owner's own photograph.
 */
export const AI_IMAGE_POLICIES = ["generate", "enhance", "off"];
export const createBrandSchema = z.object({
    name: z.string().min(1).max(120),
    industry: z.string().max(120).optional().nullable(),
    logoUrl: z.string().url().max(2000).optional().nullable(),
    /** Defaults to the workspace's country/timezone when omitted. */
    country: z.string().length(2).optional(),
    timezone: z.string().max(80).optional(),
    cart10StoreId: z.string().max(100).optional().nullable(),
    /**
     * Optional rather than defaulted: a zod default is applied on the OUTPUT
     * type, which would make the field mandatory for every caller constructing
     * a CreateBrandInput. The column's own default covers the omitted case.
     */
    aiImagePolicy: z.enum(AI_IMAGE_POLICIES).optional(),
});
export const updateBrandSchema = createBrandSchema.partial();
// =============================================================================
// Brand profile — the facts, as opposed to the voice (spec §6: "inputs to
// every generation").
//
// Every field optional. The screen asks a lot of questions, but a brand that
// answers none still generates posts; each answer removes one more thing the
// model has to write around, and the ones under "facts" are what it is
// allowed to state at all.
// =============================================================================
/** Keeps a list field from growing without bound in the prompt. */
const shortList = (max, itemMax = 200) => z.array(z.string().trim().min(1).max(itemMax)).max(max).default([]);
const optionalText = (max) => z.string().trim().max(max).optional().nullable();
export const PRICE_POSITIONS = ["budget", "mid", "premium"];
export const brandProfileSchema = z.object({
    description: optionalText(600),
    story: optionalText(1500),
    mission: optionalText(600),
    differentiators: shortList(10, 300),
    pricePositioning: z.enum(PRICE_POSITIONS).optional().nullable(),
    products: shortList(30),
    bestSellers: shortList(15),
    customerNeeds: optionalText(800),
    objections: shortList(10, 300),
    location: optionalText(300),
    serviceArea: optionalText(300),
    openingHours: optionalText(300),
    contactPhone: optionalText(60),
    website: optionalText(300),
    deliveryInfo: optionalText(500),
    paymentMethods: shortList(12, 80),
    preferredCta: optionalText(200),
    imagePeople: z.enum(IMAGE_PEOPLE_OPTIONS).optional().nullable(),
    /**
     * Visual identity. Length-capped, not format-checked.
     *
     * A colour that is not a valid hex string reaches the image prompt as a word
     * and is ignored by the model, which is a far better outcome than refusing
     * to save a brand profile because someone typed "dark green".
     */
    primaryColor: optionalText(40),
    supportingColors: shortList(5, 40),
    photographyStyle: optionalText(400),
    topicsToAvoid: shortList(15, 200),
    complianceNotes: optionalText(1000),
    /**
     * The long-form catch-all. Capped because it rides in the cached prompt
     * prefix on every generation for this brand: generous enough for a real
     * description, bounded enough that it cannot quietly double the cost of
     * every post.
     */
    factSheet: optionalText(5000),
});
/**
 * Selectable tones, stored as part of the free-text `tone` string.
 *
 * Not a database enum: a brand's tone is genuinely open-ended ("warm, but
 * never gushing"), and an enum would force everyone into twelve boxes. These
 * are the common answers offered as one tap, joined with anything the owner
 * types. `parseTone` and `formatTone` are the round trip.
 */
export const TONE_PRESETS = [
    "friendly",
    "professional",
    "warm",
    "playful",
    "confident",
    "calm",
    "witty",
    "direct",
    "luxurious",
    "inspiring",
    "expert",
    "youthful",
];
/** Splits a stored tone back into chosen presets plus whatever else was typed. */
export function parseTone(tone) {
    const parts = (tone ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const presets = [];
    const rest = [];
    for (const part of parts) {
        const match = TONE_PRESETS.find((p) => p === part.toLowerCase());
        if (match && !presets.includes(match))
            presets.push(match);
        else
            rest.push(part);
    }
    return { presets, extra: rest.join(", ") };
}
/** The inverse. Presets first so the stored order is stable and cache-friendly. */
export function formatTone(presets, extra) {
    // Preset order follows TONE_PRESETS, not click order: the voice block is a
    // cached prompt prefix, and "warm, friendly" vs "friendly, warm" would be a
    // different prefix for the same meaning.
    const ordered = TONE_PRESETS.filter((p) => presets.includes(p));
    return [...ordered, extra.trim()].filter(Boolean).join(", ");
}
export const brandVoiceSchema = z.object({
    /** Null = the brand default; set = an override for one connected page. */
    socialAccountId: z.string().uuid().optional().nullable(),
    tone: z.string().max(300).optional().nullable(),
    audience: z.string().max(300).optional().nullable(),
    /** BCP-47 — carries dialect as well as language (en-GB, es-MX, en-GH). */
    language: z.string().min(2).max(20).default("en"),
    dialectNotes: z.string().max(500).optional().nullable(),
    wordsToUse: z.array(z.string().max(60)).max(40).default([]),
    wordsToAvoid: z.array(z.string().max(60)).max(40).default([]),
    samplePosts: z.array(z.string().max(1000)).max(10).default([]),
    emojiLevel: z.enum(["none", "some", "lots"]).default("some"),
    hashtagStyle: z.enum(["none", "few", "many"]).default("few"),
});
export const generatePostSchema = z.object({
    brandId: z.string().uuid(),
    /** Which connected pages to write for; defaults to all active ones. */
    socialAccountIds: z.array(z.string().uuid()).max(20).optional(),
    topic: z.string().min(3).max(500),
    /** Facts the model is allowed to state — prices, offers, dates. */
    facts: z.array(z.string().max(300)).max(20).optional(),
    /** Generate an image as well as captions. */
    withImage: z.boolean().default(false),
    /** Use an existing media asset instead of generating one. */
    mediaAssetId: z.string().uuid().optional().nullable(),
});
export const previewVoiceSchema = z.object({
    brandId: z.string().uuid(),
    /** Unsaved voice being edited, so "preview a sample post" needs no save. */
    voice: brandVoiceSchema.partial().optional(),
    topic: z.string().min(3).max(500).default("Introduce the business"),
});
// =============================================================================
// AI output schema (spec §6: "Claude returns structured JSON (validate with
// zod)"). Declared in phase 1 so the generator in phase 3 has one contract to
// code against, and so a malformed model response fails loudly at the boundary
// rather than producing an empty caption that reaches a real page.
// =============================================================================
export const generatedVariantSchema = z.object({
    platform: z.enum(["facebook", "instagram", "threads", "linkedin", "x"]),
    /**
     * Cleaned here rather than at the call sites.
     *
     * Three paths generate captions (realtime, the batch API, campaigns) and
     * every one of them parses through this schema, so this is the only place
     * the clean-up cannot be forgotten. See captions.ts for what it removes and
     * why the prompt instruction alone is not enough.
     */
    caption: z.string().min(1).max(5000).transform(cleanCaption),
    hashtags: z.array(z.string().max(60)).max(30).default([]),
});
export const generatedPostSchema = z.object({
    variants: z.array(generatedVariantSchema).min(1),
    image_prompt: z.string().max(2000).optional().nullable(),
    /**
     * The visual approach the model chose, from CREATIVE_STRATEGIES.
     *
     * `catch` rather than a strict enum: an unrecognised value means the model
     * invented a strategy name, which is a reason to fall back to generic art
     * direction and not a reason to throw away a whole generation that has
     * already been paid for.
     */
    creative_strategy: z
        .enum(CREATIVE_STRATEGIES)
        .nullable()
        .optional()
        .catch(null),
    /**
     * The catalogue product the model says its picture will show, or null.
     *
     * Free text and tolerated as such: it is matched back to a row by name on
     * the server, which is deliberately forgiving because the model paraphrases
     * ("snapper" for "Red snapper, 1kg carton"). `catch` for the same reason
     * `creative_strategy` has one — a malformed value here is a reason to fall
     * back to an invented image, never to throw away captions already paid for.
     */
    featured_product: z.string().max(200).nullable().optional().catch(null),
});
/**
 * The picture-only response, for giving an already-written post an image.
 *
 * `image_prompt` is required here, unlike above: the whole request is for a
 * picture, so a response without one is a failure rather than a choice. And
 * there is no `variants` field at all — a model that cannot return a caption
 * cannot change one, which is the guarantee this flow needs.
 */
export const generatedImagePromptSchema = z.object({
    image_prompt: z.string().min(1).max(2000),
    creative_strategy: z.enum(CREATIVE_STRATEGIES).nullable().optional().catch(null),
    featured_product: z.string().max(200).nullable().optional().catch(null),
});
//# sourceMappingURL=schemas.js.map